/**
 * Real-world text measurement.
 *
 * We measure the glyph bounding box of the text in the chosen font at a large
 * reference pixel size using the Canvas 2D API, then use that box's aspect
 * ratio to convert a physical height (in cm) into a physical width (in cm).
 * Because the measurement uses the actual font metrics of each typeface,
 * the same text at the same height comes out narrower in a condensed font
 * (e.g. Oswald) than in a wide one (e.g. Verdana) - matching how it would
 * really be cut/printed.
 */

export const REFERENCE_PX = 300;

// Fallback cap-height ratio used only if the browser can't report a real
// glyph bounding box (e.g. text is empty/whitespace-only).
const FALLBACK_HEIGHT_RATIO = 0.72;

let sharedCanvas: HTMLCanvasElement | null = null;

function getContext(): CanvasRenderingContext2D | null {
  if (typeof document === "undefined") return null;
  if (!sharedCanvas) sharedCanvas = document.createElement("canvas");
  return sharedCanvas.getContext("2d");
}

export interface RawTextBox {
  widthPx: number;
  heightPx: number;
}

/** Measures the text's bounding box at `sizePx` for `fontFamily`. */
export function measureRawBox(
  text: string,
  fontFamily: string,
  weight: number,
  sizePx: number = REFERENCE_PX
): RawTextBox {
  const ctx = getContext();
  const sample = text.length > 0 ? text : "Text";
  if (!ctx) {
    // Server-side / no canvas support: fall back to a rough estimate so
    // layout code has *something* sane to render before hydration.
    return {
      widthPx: sample.length * sizePx * 0.55,
      heightPx: sizePx * FALLBACK_HEIGHT_RATIO,
    };
  }
  ctx.font = `${weight} ${sizePx}px ${fontFamily}`;
  const m = ctx.measureText(sample);
  const ascent = m.actualBoundingBoxAscent;
  const descent = m.actualBoundingBoxDescent;
  let heightPx = (ascent ?? 0) + (descent ?? 0);
  if (!heightPx || Number.isNaN(heightPx) || !Number.isFinite(heightPx)) {
    heightPx = sizePx * FALLBACK_HEIGHT_RATIO;
  }
  let widthPx = m.width;
  if (!widthPx || Number.isNaN(widthPx)) {
    widthPx = sample.length * sizePx * 0.55;
  }
  return { widthPx, heightPx };
}

export interface PhysicalSize {
  widthCm: number;
  heightCm: number;
}

/**
 * Given a desired real-world text height (cm), returns the real-world width
 * (cm) implied by the font's actual glyph proportions for this text.
 */
export function computePhysicalSize(
  text: string,
  fontFamily: string,
  weight: number,
  heightCm: number
): PhysicalSize {
  const box = measureRawBox(text, fontFamily, weight, REFERENCE_PX);
  const cmPerPx = box.heightPx > 0 ? heightCm / box.heightPx : 0;
  const widthCm = box.widthPx * cmPerPx;
  return {
    widthCm: roundTo(widthCm, 1),
    heightCm: roundTo(heightCm, 1),
  };
}

export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function formatCm(value: number): string {
  return `${value.toLocaleString("de-CH", {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} cm`;
}
